import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router';
import Button from '@mui/material/Button';
import { Box } from '@mui/system';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid';

import { QueryRenderer } from '@barboxx/admin-components';
import { usePaging, useSearchString } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Can, useBool } from '@barboxx/react-utils';

import {
    CustomPageHeader,
    RootContainer,
} from '../../components/nav/RootContainer';
import { CreateProductLineDialog } from './component/CreateDialog';
import { ProductLineTable } from './component/ProductLinesTable';

export function ProductLinesList() {
    const { page, size, updatePageModel, sort, updateSort } = usePaging();
    const navigate = useNavigate();
    const [createOpen, createActions] = useBool();

    const { InputComponent, searchString } = useSearchString();

    const productLineQuery = useQuery({
        queryKey: ['productLines', page, size, sort, searchString],
        queryFn: () =>
            Api.Products.ProductLines.getProductLines({
                page,
                size,
                sort,
                searchString,
            }),
    });

    const handleChangePageModel = (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => {
        updatePageModel(model);
    };

    const handleRowClick = (id: string) => {
        navigate(`${id}`);
    };

    const handleCreate = React.useCallback(() => {
        createActions.on();
    }, [createActions]);

    const handleSuccess = () => {
        productLineQuery.refetch();
    };

    const CustomPageToolbarComponent = React.useCallback(
        () => (
            <CustomPageHeader>
                <Box gap={2} flexDirection="row" display="flex">
                    <InputComponent />
                    <Can I="create" a="ProductLine">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                        >
                            Add ProductLine
                        </Button>
                    </Can>
                </Box>
            </CustomPageHeader>
        ),
        [InputComponent, handleCreate]
    );

    return (
        <RootContainer
            maxWidth="md"
            slots={{ header: CustomPageToolbarComponent }}
        >
            <QueryRenderer results={productLineQuery}>
                {data => (
                    <ProductLineTable
                        data={data?.results ?? []}
                        size={size}
                        handleChangePageModel={handleChangePageModel}
                        onRowClick={handleRowClick}
                        onChangeSort={updateSort}
                        page={page}
                        total={data?.total ?? 0}
                    />
                )}
            </QueryRenderer>

            <CreateProductLineDialog
                open={createOpen}
                onClose={createActions.off}
                onSuccess={handleSuccess}
            />
        </RootContainer>
    );
}
